"use client";

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

import { FallbackImage } from "@/components/fallback-image";
import { DiscVisual } from "@/components/media-visuals";
import { cn } from "@/lib/utils";
import { categorizeFormat } from "@garimpa/lib/formatClassification";
import {
  type FormatCategoryWithOther as FormatCategory,
  type FormatCategoryWithOther,
} from "@garimpa/lib/formatGroups";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";

interface MediaContextValue {
  category: FormatCategory;
  imageUrl: string | null;
  title: string;
  artistName: string | null;
  markCoverReady: () => void;
}

const MediaContext = createContext<MediaContextValue | null>(null);

function useMediaContext() {
  const ctx = useContext(MediaContext);
  if (!ctx) {
    throw new Error("AlbumCoverWithMedia.Media/Cover must be used within <AlbumCoverWithMedia>");
  }
  return ctx;
}

interface AlbumCoverWithMediaProps {
  format?: string | null;
  formatCategory?: FormatCategoryWithOther;
  imageUrl: string | null;
  title: string;
  artistName?: string | null;
  children: ReactNode;
  className?: string;
}

function AlbumCoverWithMediaRoot({
  format,
  formatCategory,
  imageUrl,
  title,
  artistName = null,
  children,
  className,
}: AlbumCoverWithMediaProps) {
  const category = formatCategory ?? categorizeFormat(format ?? null) ?? "other";
  const [readyImageUrl, setReadyImageUrl] = useState<string | null>(null);
  const coverReady = !imageUrl || readyImageUrl === imageUrl;
  const markCoverReady = useCallback(() => setReadyImageUrl(imageUrl), [imageUrl]);

  return (
    <MediaContext.Provider value={{ category, imageUrl, title, artistName, markCoverReady }}>
      <div
        className={cn(
          "album-cover-with-media relative w-[84%] sm:w-full aspect-square max-w-[450px]",
          className,
        )}
        data-cover-ready={coverReady}
      >
        {children}
      </div>
    </MediaContext.Provider>
  );
}

function Media() {
  const { category, title, artistName } = useMediaContext();
  return <DiscVisual category={category} title={title} artistName={artistName} />;
}

function Cover({ className, priority = true }: { className?: string; priority?: boolean }) {
  const { imageUrl, title, markCoverReady } = useMediaContext();

  return (
    <div
      className={cn(
        "cover-entry relative z-10 w-full h-full rounded-lg shadow-2xl overflow-hidden bg-secondary ring-1 ring-border",
        className,
      )}
    >
      <FallbackImage
        src={withImageRenderParams(imageUrl, { width: 640 })}
        srcSet={buildImageSrcSet(imageUrl, [320, 480, 640, 960])}
        alt={title}
        priority={priority}
        sizes="(min-width: 768px) 50vw, 100vw"
        onLoad={markCoverReady}
        onError={markCoverReady}
      />
    </div>
  );
}

export const AlbumCoverWithMedia = Object.assign(AlbumCoverWithMediaRoot, {
  Media,
  Cover,
});
